import React from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion'
import { Input } from '../components/ui/input'
import {
  Check,
  ChevronRight,
  Star,
  Users,
  Ticket,
  LineChart,
  Sparkles,
  Shield,
  ShoppingCart,
  Smartphone,
  Zap,
} from 'lucide-react'

export default function SportsManagerLanding() {
  const navigate = useNavigate()

  return (
    <div className="min-vh-100 w-100" style={{ background: 'linear-gradient(#f8fafc, #ffffff)' }}>
      {/* NAVBAR */}
      <header className="position-sticky top-0 z-3 w-100 border-bottom bg-white bg-opacity-75 header-blur">
        <div className="container d-flex align-items-center justify-content-between py-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
              style={{ width: 36, height: 36 }}
            >
              <span className="fw-bold">SM</span>
            </div>
            <span className="fs-5 fw-bold">SPORTSMANAGER.FUN</span>
          </div>

          <nav className="d-none d-md-flex gap-4">
            <a href="#features" className="text-secondary text-decoration-none fw-medium">
              Features
            </a>
            <a href="#solutions" className="text-secondary text-decoration-none fw-medium">
              Solutions
            </a>
            <a href="#pricing" className="text-secondary text-decoration-none fw-medium">
              Pricing
            </a>
            <a href="#faq" className="text-secondary text-decoration-none fw-medium">
              FAQ
            </a>
          </nav>

          <div className="d-none d-md-flex align-items-center gap-2">
            <Button variant="ghost" className="rounded-3" onClick={() => navigate('/login')}>
              Log in
            </Button>
            <Button className="rounded-3" onClick={() => navigate('/register')}>
              Get Started <ChevronRight className="ms-1" size={16} />
            </Button>
          </div>

          <Button
            variant="outline"
            className="d-md-none rounded-3"
            onClick={() => navigate('/login')}
          >
            Menu
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section className="position-relative overflow-hidden sm-hero-bg py-5 py-md-6">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-12 col-md-6">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="fw-bold display-6"
              >
                Run your club like a pro. Make it <span className="text-gradient">fun</span> for fans.
              </motion.h1>

              <p className="mt-3 fs-5 text-secondary">
                SPORTSMANAGER.FUN is the all-in-one SaaS for sports clubs: ticketing, merchandising,
                CRM, fan engagement, and sponsor marketplace — unified across web & app.
              </p>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                <Button className="px-4 rounded-3" onClick={() => navigate('/login')}>
                  Start free trial <ChevronRight className="ms-1" size={16} />
                </Button>
                <Button
                  variant="outline"
                  className="px-4 rounded-3"
                  onClick={() => navigate('/register')}
                >
                  Book a demo
                </Button>
              </div>

              <div className="d-flex align-items-center gap-2 text-secondary mt-3">
                <Star size={16} fill="#facc15" color="#facc15" />
                <span className="small">Trusted by teams from academy to pro leagues</span>
              </div>
            </div>

            <div className="col-12 col-md-6 position-relative">
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="ratio ratio-16x9 rounded-3 border bg-white bg-opacity-75 p-2 shadow">
                  <div className="w-100 h-100 rounded-3 sm-card-gradient p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-1">
                        <div
                          className="rounded-circle"
                          style={{ width: 10, height: 10, background: '#fb7185' }}
                        />
                        <div
                          className="rounded-circle"
                          style={{ width: 10, height: 10, background: '#f59e0b' }}
                        />
                        <div
                          className="rounded-circle"
                          style={{ width: 10, height: 10, background: '#10b981' }}
                        />
                      </div>
                      <div className="small text-secondary fw-medium">Dashboard • Match Day</div>
                    </div>

                    <div className="row g-2 mt-2">
                      <div className="col-12 col-md-7">
                        <Card className="rounded-3 h-100">
                          <CardHeader className="pb-0 bg-white">
                            <CardTitle className="d-flex align-items-center gap-2 fs-6">
                              <LineChart size={16} /> Live Sales
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="row text-center g-2">
                              {[
                                ['Tickets', '1,842'],
                                ['Merch GMV', '€12.9k'],
                                ['Check-ins', '2,311'],
                              ].map(([k, v]) => (
                                <div className="col-4" key={k}>
                                  <div className="border rounded-3 p-2 bg-light">
                                    <div className="small text-secondary">{k}</div>
                                    <div className="fw-semibold">{v}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="rounded-3 repeating-stripes mt-3" style={{ height: 96 }} />
                          </CardContent>
                        </Card>
                      </div>

                      <div className="col-12 col-md-5">
                        <Card className="rounded-3 h-100">
                          <CardHeader className="pb-0 bg-white">
                            <CardTitle className="d-flex align-items-center gap-2 fs-6">
                              <Ticket size={16} /> Next Game
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="rounded-3 p-3" style={{ background: '#eef2ff' }}>
                              <div className="fw-semibold small">HLA Alicante vs Oviedo</div>
                              <div className="text-secondary small mt-1">
                                Fri 20:45 • Section B • From €9
                              </div>
                              <Button className="w-100 mt-2 btn-primary btn-sm rounded-2">
                                Sell Tickets
                              </Button>
                            </div>
                            <div className="row g-2 mt-2 small">
                              <div className="col-6">
                                <div className="bg-light border rounded-3 p-2">
                                  Open Rate: <span className="fw-semibold">42%</span>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="bg-light border rounded-3 p-2">
                                  CTR: <span className="fw-semibold">9.1%</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="position-absolute end-0 translate-middle-y" style={{ bottom: '-1rem' }}>
                  <div className="d-none d-md-block bg-white bg-opacity-75 border shadow-sm rounded-3 p-2 small">
                    <Users size={16} /> 18,402 fans this season
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-top border-bottom bg-white py-3">
        <div className="container d-flex flex-wrap align-items-center justify-content-center gap-4">
          {['Ticketing 3D', 'Fan CRM', 'Merch & POS', 'Sponsor Marketplace', 'Automations', 'Analytics'].map(
            (t) => (
              <div key={t} className="text-uppercase small fw-semibold text-secondary">
                {t}
              </div>
            ),
          )}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-5">
        <div className="container">
          <div className="text-center mx-auto" style={{ maxWidth: 640 }}>
            <h2 className="fw-bold fs-2">Everything your club needs in one place</h2>
            <p className="text-secondary">
              From discovery to renewal — unify the fan journey and your back-office.
            </p>
          </div>

          <div className="row g-3 mt-3">
            {[
              { icon: <Ticket size={18} />, title: 'Smart Ticketing', desc: '3D seat maps, dynamic pricing, check-in & scalper-safe QR codes.' },
              { icon: <ShoppingCart size={18} />, title: 'Merchandising', desc: 'Unified shop & in-arena POS. Bundles, flash deals, and stock alerts.' },
              { icon: <Users size={18} />, title: 'Fan CRM', desc: 'RFM segments, journeys, and 1-click campaigns across email, SMS & WhatsApp.' },
              { icon: <LineChart size={18} />, title: 'Analytics', desc: 'Real-time dashboards for ops, revenue and sponsor ROI.' },
              { icon: <Smartphone size={18} />, title: 'Web + App', desc: 'One data model, consistent experiences on every device.' },
              { icon: <Shield size={18} />, title: 'Security', desc: 'GDPR-ready, SSO, and role-based permissions.' },
            ].map(({ icon, title, desc }) => (
              <div className="col-12 col-md-6 col-lg-4" key={title}>
                <Card className="rounded-3 h-100">
                  <CardHeader className="pb-0 bg-white">
                    <CardTitle className="d-flex align-items-center gap-2 fs-5">
                      {icon}
                      <span>{title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-secondary">{desc}</CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section id="solutions" className="py-4">
        <div className="container">
          <div className="row g-3">
            {[
              { title: 'Match Day Ops', bullets: ['scan-to-enter', 'real-time occupancy', 'cashless upgrades'], tint: '#ecfdf5' },
              { title: 'Engage & Grow', bullets: ['loyalty coins', 'quests & gamification', 'referrals'], tint: '#eef2ff' },
              { title: 'Sponsor Hub', bullets: ['self-serve offers', 'geo-push near arena', 'ROI dashboards'], tint: '#fffbeb' },
            ].map(({ title, bullets, tint }) => (
              <div className="col-12 col-md-4" key={title}>
                <div className="border rounded-4 shadow-sm p-4" style={{ background: `linear-gradient(#ffffff, ${tint})` }}>
                  <div className="d-flex align-items-center gap-2 fw-semibold">
                    <Sparkles size={18} /> {title}
                  </div>
                  <ul className="mt-2 list-unstyled text-secondary">
                    {bullets.map((b) => (
                      <li className="d-flex align-items-center gap-2" key={b}>
                        <Check size={16} color="#059669" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-5">
        <div className="container">
          <div className="text-center mx-auto" style={{ maxWidth: 640 }}>
            <h2 className="fw-bold fs-2">Simple pricing that scales with you</h2>
            <p className="text-secondary">Start free. Upgrade when you’re winning.</p>
          </div>

          <div className="row g-3 mt-3">
            {[
              { name: 'Starter', price: '€0', blurb: 'For academies & small clubs', perks: ['500 contacts', 'Basic ticketing', 'Shop & POS', 'Email campaigns'] },
              { name: 'Growth', price: '€249/mo', blurb: 'For ambitious teams', perks: ['10k contacts', 'Dynamic pricing', 'Automation', 'Sponsor hub'], featured: true },
              { name: 'Pro +', price: 'Custom', blurb: 'For pro leagues & federations', perks: ['Unlimited contacts', 'SSO & roles', 'Premium SLA', 'API access'] },
            ].map(({ name, price, blurb, perks, featured }) => (
              <div className="col-12 col-md-4" key={name}>
                <Card className={`rounded-4 h-100 ${featured ? 'border-primary shadow' : ''}`}>
                  <CardHeader>
                    <CardTitle className="fs-4">{name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="display-6 fw-bolder">{price}</div>
                    <div className="text-secondary small">{blurb}</div>
                    <ul className="mt-3 list-unstyled">
                      {perks.map((p) => (
                        <li key={p} className="d-flex align-items-center gap-2 small">
                          <Check size={16} color="#059669" /> {p}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-100 mt-3">Choose plan</Button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIPE */}
      <section className="py-4">
        <div className="container">
          <div
            className="border rounded-4 text-white shadow p-4"
            style={{ background: 'linear-gradient(90deg,#4f46e5,#7c3aed)' }}
          >
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-6">
                <h3 className="fw-bold h4">Launch your next season with confidence</h3>
                <p className="mb-2 text-opacity-75">
                  Spin up ticketing, shop, and mobile app in days — not months.
                </p>
                <div className="d-flex align-items-center gap-2">
                  <Zap size={16} /> Fast onboarding • Zero setup fees
                </div>
              </div>
              <div className="col-12 col-md-6">
                <form className="d-flex flex-column flex-md-row gap-2" onSubmit={(e) => e.preventDefault()}>
                  <Input placeholder="Work email" className="bg-white" />
                  <Button className="bg-white text-dark border-0">Get invite</Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5">
        <div className="container" style={{ maxWidth: 800 }}>
          <h2 className="text-center fw-bold fs-2">Frequently asked questions</h2>
          <Accordion className="mt-3">
            {[
              [
                'Can we import our existing fans and orders?',
                'Yes — CSV importers and API endpoints help you migrate contacts, orders, and ticket history in minutes.',
              ],
              [
                'Do you support both web and mobile app?',
                'Absolutely. SPORTSMANAGER.FUN ships with a unified data model for web & native app so journeys are consistent.',
              ],
              ['Is the platform GDPR compliant?', 'We provide DPA, consent logs, data export/delete tooling, and EU hosting options.'],
              ['How long to go live?', 'Clubs often launch essentials within 2 weeks, with advanced automations added progressively.'],
            ].map(([q, a], i) => (
              <AccordionItem key={i}>
                <AccordionTrigger>{q}</AccordionTrigger>
                <AccordionContent className="text-secondary">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-top bg-white py-4">
        <div className="container">
          <div className="row g-4">
            <div className="col-12 col-md-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 bg-primary text-white"
                  style={{ width: 32, height: 32 }}
                >
                  <span className="fw-bold small">SM</span>
                </div>
                <span className="fw-semibold">SPORTSMANAGER.FUN</span>
              </div>
              <p className="mt-2 small text-secondary">
                All-in-one SaaS for sports clubs to sell more, engage better, and operate smarter.
              </p>
            </div>

            <div className="col-6 col-md-3">
              <div className="small fw-semibold">Product</div>
              <ul className="list-unstyled small mt-2">
                <li>
                  <a href="#features" className="text-secondary text-decoration-none">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#solutions" className="text-secondary text-decoration-none">
                    Solutions
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-secondary text-decoration-none">
                    Pricing
                  </a>
                </li>
              </ul>
            </div>

            <div className="col-6 col-md-3">
              <div className="small fw-semibold">Company</div>
              <ul className="list-unstyled small mt-2">
                <li>
                  <a className="text-secondary text-decoration-none">About</a>
                </li>
                <li>
                  <a className="text-secondary text-decoration-none">Careers</a>
                </li>
                <li>
                  <a className="text-secondary text-decoration-none">Contact</a>
                </li>
              </ul>
            </div>

            <div className="col-12 col-md-3">
              <div className="small fw-semibold">Legal</div>
              <ul className="list-unstyled small mt-2">
                <li>
                  <a className="text-secondary text-decoration-none">Privacy</a>
                </li>
                <li>
                  <a className="text-secondary text-decoration-none">Terms</a>
                </li>
                <li>
                  <a className="text-secondary text-decoration-none">Security</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3 small text-secondary">
            <span>© {new Date().getFullYear()} SPORTSMANAGER.FUN</span>
            <span>Made for clubs that love data and fans that love fun.</span>
          </div>
        </div>
      </footer>
    </div>
  )
}
